"use client";

import { createContext, useCallback, useContext, useReducer, useEffect, useMemo } from "react";
import { DEFAULT_MANUSCRIPT_MARGIN, MANUSCRIPT_MARGIN_STORAGE_KEY, loadManuscriptMargin } from "@/src/lib/manuscriptLayout";
import { LOCAL_STORAGE_SYNC_EVENT } from "@/src/hooks/useLocalStorageState";
import { loadChatAgentEnabled } from "@/src/helpers/commentVisibility";
import { notifyEditorContentChanged, resetEditorContentChangeSignal } from "@/src/stores/editorContentChangeStore";

const EditorContext = createContext();

const initialState = {
  title: "",
  bookId: 0,
  books: [],
  booksMetadata: [],
  chapterId: 0,
  chapters: [],
  subscription: {
    subscription_type: "basic",
    is_active: true,
  },
  usageSummary: null,
  isUserReady: false,
  // The stored choice: a theme name, or "system" (ThemeProvider resolves it).
  theme: "system",
  manuscriptMargin: DEFAULT_MANUSCRIPT_MARGIN,
  openExpressionAllowed: false,
  openExpressionEnabled: false,
  editor: null,
  // Selections on their way to a companion tab. Queued rather than pushed:
  // those panels only mount when their tab is showing, so there is nothing to
  // call at the moment the author clicks Imagine / Ask.
  pendingImagine: null,
  pendingAskQuarkle: null,
  isStudioLoading: false,
  studioLoadingError: {},
  isStudioReady: false,
  isEditorReady: false,
  isSaving: false,
  wordThreshold: 120,
  reviewAgents: [],
  // Chat pseudo-agent: whether chat's margin notes are visible. Client-side
  // only (persisted in localStorage) — "Chat" is not a server-side reviewer.
  chatAgentEnabled: true,
  // Comment focus, shared between the margin rail and the review room:
  // which note's card is pinned open (null = none), and the latest
  // navigate-to request ({id, nonce} — the nonce lets a repeat click on
  // the same note re-fire the rail's scroll+pin).
  focusedCommentId: null,
  commentFocusRequest: null,
  role: null,
  is_public: false,
  // The version manager / diff viewer takes over the manuscript area when open.
  // Store only the selected checkpoint identity here. Chapter version data has
  // its own request-safe external store and should not be broadcast with every
  // editor context update.
  diffViewOpen: false,
  diffVersionId: null,
  diffViewingCurrent: false,
  diffCompareMode: "chrono",
  leftPanelView: "chapters",
};

function reducer(state, action) {
  switch (action.type) {
    case "books/set":
      return { ...state, books: action.payload };
    case "books/upsertMany": {
      // Upsert many books into the state using the book id as the key
      // It works by creating a map of the books by id and then upserting the books into the state
      const incoming = Array.isArray(action.payload) ? action.payload : [];
      if (incoming.length === 0) return state;
      const byId = new Map((state.books || []).map((b) => [b?.id, b]));
      incoming.forEach((b) => {
        if (b?.id == null) return;
        byId.set(b.id, b);
      });
      return { ...state, books: Array.from(byId.values()) };
    }
    case "books/add":
      return { ...state, books: [...state.books, action.payload] };
    case "books/removeById":
      return { ...state, books: state.books.filter((b) => b.id !== action.payload) };

    case "booksMetadata/set":
      return { ...state, booksMetadata: action.payload };
    case "booksMetadata/add":
      return { ...state, booksMetadata: [...state.booksMetadata, action.payload] };
    case "booksMetadata/removeById":
      return { ...state, booksMetadata: state.booksMetadata.filter((b) => b.id !== action.payload) };
    case "booksMetadata/patch":
      return {
        ...state,
        booksMetadata: state.booksMetadata.map((b) => (b.id === action.payload.id ? { ...b, ...action.payload } : b)),
      };
    case "book/patch":
      return {
        ...state,
        books: state.books.map((book) => (book.id === action.payload.id ? { ...book, ...action.payload } : book)),
      };
    case "book/targetWordCount/set":
      return {
        ...state,
        books: state.books.map((book) =>
          book.id === action.payload.bookId ? { ...book, target_word_count: action.payload.newTargetWordCount } : book,
        ),
      };
    case "title/set":
      return { ...state, title: action.payload };
    case "bookId/set":
      return { ...state, bookId: action.payload };
    case "bookId/reset":
      return { ...state, bookId: null };
    case "subscription/set":
      return { ...state, subscription: action.payload };
    case "usageSummary/set":
      return { ...state, usageSummary: action.payload };
    case "user/ready/set":
      return { ...state, isUserReady: action.payload };
    case "chapterId/set": {
      const wordThreshold =
        state.chapters.find((chapter) => chapter.id === action.payload)?.word_count_trigger ?? initialState.wordThreshold;
      if (state.bookId && action.payload !== 0) {
        localStorage.setItem(`active-chapter-${state.bookId}`, action.payload);
      }
      return {
        ...state,
        chapterId: action.payload,
        wordThreshold,
        diffVersionId: action.payload === state.chapterId ? state.diffVersionId : null,
        diffViewingCurrent: action.payload === state.chapterId ? state.diffViewingCurrent : false,
        diffCompareMode: action.payload === state.chapterId ? state.diffCompareMode : "chrono",
      };
    }
    case "chapters/set": {
      const orderedChapters = [...action.payload].sort((a, b) => a.chapterNumber - b.chapterNumber);
      return { ...state, chapters: orderedChapters };
    }
    case "chapters/update":
      var updatedChapters = state.chapters.map((chapter) => {
        if (chapter.id === action.payload.chapterId) {
          return {
            ...chapter,
            content: action.payload.html,
            updatedAt: action.payload.updatedAt,
          };
        }
        return chapter;
      });
      return { ...state, chapters: updatedChapters };
    case "chapters/addMany":
      return { ...state, chapters: [...state.chapters, ...action.payload] };
    case "chapters/delete": {
      // The server closes the gap the same way, so the numbers stay the ones the agent reads.
      const rest = state.chapters.filter((chapter) => chapter.id !== action.payload);
      return { ...state, chapters: rest.map((chapter, i) => ({ ...chapter, chapterNumber: i + 1 })) };
    }
    case "chapters/updateTitle":
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter.id === action.payload.chapterId ? { ...chapter, title: action.payload.newTitle } : chapter,
        ),
      };
    case "theme/set":
      return { ...state, theme: action.payload };
    case "manuscriptMargin/set":
      return { ...state, manuscriptMargin: action.payload };
    case "openExpressionAllowed/set":
      return { ...state, openExpressionAllowed: action.payload };
    case "openExpressionEnabled/set":
      return { ...state, openExpressionEnabled: action.payload };

    case "studio/loading/set":
      return { ...state, isStudioLoading: action.payload };
    case "studio/error/set":
      if (action.payload === "default") {
        return { ...state, studioLoadingError: { type: "default", message: "Whoops, it seems like there was an error. Please try again" } };
      } else if (action.payload === "google_login_needed") {
        return {
          ...state,
          studioLoadingError: {
            type: "google_login_needed",
            message: "This account previously logged in with Google Auth, please re-authenticate using Google",
          },
        };
      } else if (action.payload === "repeated") {
        return {
          ...state,
          studioLoadingError: { type: "repeated", message: "Seems like there's been some error. Please log out and try again." },
        };
      }
      return { ...state, studioLoadingError: { type: "error", message: action.payload } };
    case "studio/ready/set":
      return { ...state, isStudioReady: action.payload };

    case "editor/set":
      return { ...state, editor: action.payload };
    case "editor/insertContent":
      return {
        ...state,
        pendingEditorCommand: {
          type: "insertContent",
          payload: action.payload,
        },
      };
    case "editor/reset":
      return {
        ...state,
        pendingEditorCommand: {
          type: "clearContent",
        },
      };
    case "editor/refresh":
      return {
        ...state,
        title: "",
        bookId: 0,
        chapterId: 0,
        chapters: [],
        isEditorReady: false,
        editor: null,
        diffViewOpen: false,
        diffVersionId: null,
        diffViewingCurrent: false,
        diffCompareMode: "chrono",
        leftPanelView: "chapters",
      };
    case "history/open":
      return {
        ...state,
        leftPanelView: "history",
        diffViewOpen: true,
        diffVersionId: null,
        diffViewingCurrent: false,
        diffCompareMode: "chrono",
      };
    case "history/selectCheckpoint":
      return {
        ...state,
        leftPanelView: "history",
        diffViewOpen: true,
        diffVersionId: action.payload,
        diffViewingCurrent: false,
      };
    case "history/selectCurrent":
      return {
        ...state,
        leftPanelView: "history",
        diffViewOpen: true,
        diffVersionId: null,
        diffViewingCurrent: true,
        diffCompareMode: "chrono",
      };
    case "history/setCompareMode":
      return {
        ...state,
        diffCompareMode: action.payload === "current" ? "current" : "chrono",
      };
    case "editor/ready/set":
      return { ...state, isEditorReady: action.payload };
    case "editor/migrate":
      return {
        ...state,
        pendingEditorCommand: {
          type: "migrate",
          payload: action.payload,
        },
      };
    case "diffView/close":
      return {
        ...state,
        // History owns the diff workspace. Closing that workspace must also
        // return the left panel to Chapters; otherwise an editable manuscript
        // can appear beside a History timeline.
        leftPanelView: "chapters",
        diffViewOpen: false,
        diffVersionId: null,
        diffViewingCurrent: false,
        diffCompareMode: "chrono",
      };
    case "leftPanel/view":
      // Chapters and version comparison occupy the same manuscript workspace.
      // Keep that relationship atomic so the UI can never represent the invalid
      // "Chapters panel + open History comparison" combination.
      if (action.payload !== "history") {
        return {
          ...state,
          leftPanelView: action.payload,
          diffViewOpen: false,
          diffVersionId: null,
          diffViewingCurrent: false,
          diffCompareMode: "chrono",
        };
      }
      return { ...state, leftPanelView: action.payload };
    case "role/set":
      return { ...state, role: action.payload };
    case "is_public/set":
      return { ...state, is_public: action.payload };
    case "editor/clearPendingCommand":
      return {
        ...state,
        pendingEditorCommand: null,
      };
    case "wordThreshold/set": {
      const updatedChaptersWithThreshold = state.chapters.map((chapter) => {
        if (chapter.id === state.chapterId) {
          return { ...chapter, word_count_trigger: action.payload };
        }
        return chapter;
      });
      return {
        ...state,
        chapters: updatedChaptersWithThreshold,
        wordThreshold: action.payload,
      };
    }
    case "reviewAgents/set":
      return { ...state, reviewAgents: action.payload };
    case "chatAgentEnabled/set":
      return { ...state, chatAgentEnabled: action.payload };
    case "commentFocus/pinned":
      if (state.focusedCommentId === action.payload) return state;
      return { ...state, focusedCommentId: action.payload };
    case "commentFocus/request":
      return { ...state, commentFocusRequest: { id: action.payload, nonce: Date.now() } };
    case "imagine/set":
      return { ...state, pendingImagine: action.payload };
    case "imagine/clear":
      return { ...state, pendingImagine: null };
    case "askQuarkle/set":
      return { ...state, pendingAskQuarkle: action.payload };
    case "askQuarkle/clear":
      return { ...state, pendingAskQuarkle: null };
    case "saving/set":
      return { ...state, isSaving: action.payload };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

export function EditorContextProvider({ children }) {
  const [state, reducerDispatch] = useReducer(reducer, {
    ...initialState,
    pendingEditorCommand: null,
  });
  const dispatch = useCallback((action) => {
    if (action.type === "content/changed") {
      notifyEditorContentChanged();
      return;
    }
    if (action.type === "editor/refresh") {
      resetEditorContentChangeSignal();
    }
    reducerDispatch(action);
  }, []);

  useEffect(() => {
    const savedMargin = loadManuscriptMargin();
    if (savedMargin !== DEFAULT_MANUSCRIPT_MARGIN) {
      dispatch({ type: "manuscriptMargin/set", payload: savedMargin });
    }
    if (!loadChatAgentEnabled()) {
      dispatch({ type: "chatAgentEnabled/set", payload: false });
    }
    // The server's copy of the margin lands in storage at login; follow it.
    const onSync = (event) => {
      if (event.detail === MANUSCRIPT_MARGIN_STORAGE_KEY) dispatch({ type: "manuscriptMargin/set", payload: loadManuscriptMargin() });
    };
    window.addEventListener(LOCAL_STORAGE_SYNC_EVENT, onSync);
    return () => window.removeEventListener(LOCAL_STORAGE_SYNC_EVENT, onSync);
  }, [dispatch]);

  useEffect(() => {
    if (state.pendingEditorCommand && state.editor) {
      const { type, payload } = state.pendingEditorCommand;

      switch (type) {
        case "insertContent":
          state.editor.commands.insertContent(payload);
          break;
        case "clearContent":
          state.editor.commands.clearContent(true);
          break;
        case "migrate":
          // Restore replaces the whole doc programmatically — tag it so it isn't
          // counted as writer churn by auto-versioning (see useAutoVersioning).
          state.editor
            .chain()
            .clearContent(true)
            .insertContent(payload)
            .command(({ tr }) => {
              tr.setMeta("contentLoad", true);
              return true;
            })
            .run();
          // Unlike an ordinary chapter load, restoring a checkpoint changes the
          // live draft. Publish only after the replacement transaction has
          // completed so the save layer captures the restored HTML.
          notifyEditorContentChanged();
          break;
      }

      dispatch({ type: "editor/clearPendingCommand" });
    }
  }, [dispatch, state.pendingEditorCommand, state.editor]);

  const value = useMemo(() => ({ ...state, dispatch }), [state, dispatch]);

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}

export function useEditorContext() {
  const context = useContext(EditorContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within a AppContextProvider");
  }
  return context;
}
